/**
 * Convert a string in presumed kebab case to camel case
 * @param {string} str description
 * @returns {string} The return string
 */
export function camelCase(str: string): string {
  return str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());
}

/**
 * Convert a string into kebab case
 * @param {string} str The value to be converted
 * @returns {string} The return string
 */
export function kebabCase(str: string): string {
  return str?.replace(/([a-z])([A-Z])/g, '$1-$2').replace(/[()]/g, '').replace(/[\s_]+/g, '-').toLowerCase();
}

/**
 * Removes all duplicate characters from a string and returns another string
 * containing ALL unique characters.  Useful for construction of REGEX objects
 * with characters from an input field, etc.
 * @param {string} str The string to process
 * @returns {string} The processed string
 */
export function removeDuplicates(str: string): string {
  return str
    .split('')
    .filter((item, pos, self) => self.indexOf(item) === pos)
    .join('');
}

/**
 * Removes all newLines from a string and replaces them with spaces
 * @param {string} str the incoming string to format
 * @returns {string} the string with newline characters replaced
 */
export function removeNewLines(str: string): string | null {
  return str?.replace(/\r?\n|\r/g, ' ') || '';
}

/**
 * Convert an attribute string into a boolean representation
 * @param {string|boolean|null} val string value from the component attribute
 * @returns {boolean} The return boolean
 */
export function stringToBool(val: string | boolean | null | undefined): boolean {
  if ((typeof val === 'string' && val.toLowerCase() === 'false') || val === false) {
    return false;
  }
  return (typeof val === 'string') || val === true;
}

/**
 * Checks if the provided value is valid.
 * @param {boolean|string|null|undefined} value - The value to check.
 * @param {boolean|undefined} acceptNullValue - Whether to return true/false when value is null
 * @returns {boolean} True if the value is valid, otherwise false.
 */
export function isValidBooleanValue(value: boolean | string | null | undefined, acceptNullValue?: boolean): boolean {
  if (value === undefined) {
    return false;
  }

  if (value === null) {
    return !!acceptNullValue;
  }

  const validValues = new Set([true, 'true', false, 'false', '']);
  return validValues.has(value);
}

/**
 * Converts an attribute string into a number, or returns NaN
 * @param {string|number|any} val string value from the component attribute
 * @returns {number} The return boolean
 */
export function stringToNumber(val?: string | number | any): number {
  return parseFloat(val); // eslint-disable-line
}

/**
 * Inject template variables in a string
 * @param {string} str The string to inject into
 * @param {any} obj The string to inject into
 * @returns {string} The return string
 */
export function injectTemplate(str: string, obj: any): string {
  // Replace all other keys with data
  return str?.replace(/\${(.*?)}/g, (_x, g) => {
    let value = obj[g];
    if (value !== undefined) return value;

    if (g.includes('.')) {
      const path = g.split('.');
      path.forEach((key: string) => {
        value = value?.[key] ?? obj[key];
      });
    }
    return value ?? '&nbsp;';
  });
}

/**
 * Combines classes and considers truthy/falsy +
 * doesn't pollute the attribs/DOM
 * @param {...any} classes classes/expressions
 * @returns {string} ` class="c1 c2..."` || ""
 */
export function buildClassAttrib(...classes: any): string {
  const classAttrib = classes.reduce((attribStr: any, c: any) => {
    if (attribStr && c) { return `${attribStr} ${c}`; }

    if (!attribStr && c) { return c; }
    return attribStr;
  }, '');

  return !classAttrib ? '' : ` class="${classAttrib}"`;
}

/**
 * Checks a keycode value and determines if it belongs to a printable character
 * @private
 * @param {number} e the event to inspect
 * @returns {boolean} Returns true if the key is a printable one.
 */
export function isPrintable(e: KeyboardEvent | any): boolean {
  const controlKeys = ['Alt', 'Shift', 'Control', 'Meta', 'CapsLock', 'Enter', 'Escape', 'Tab'];
  if (controlKeys.indexOf(e.key) > -1 || e.key?.indexOf('Arrow') > -1) {
    return false;
  }

  if ((e.altKey && (e.keyCode === 38)) || (e.keyCode > 111 && e.keyCode < 124)) {
    return false;
  }
  return true;
}

/**
 * Escape user input that will be treated as a literal string.
 * This prevents incorrect RegExp matching when converting user input.
 * @param {string} s string to process
 * @returns {string} string after escaping
 */
export function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& whole matched string
}

/**
 * Trims a text string to a specified length and adds an ellipsis if it exceeds the threshold
 * @param {string} text - The input text to be trimmed
 * @param {number} threshold - The maximum length before trimming occurs
 * @returns {string} The trimmed text with ellipsis if it exceeds the threshold, or the original text if it doesn't
 */
export function trimText(text: string, threshold: number) {
  return text.length <= threshold ? text : `${text.substr(0, threshold)}...`;
}

/**
 * Masks a text string, preserving special characters and showing only the specified number of characters at the end
 * @param {string} text - The text to mask
 * @param {number} visibleChars - Number of characters to leave visible at the end
 * @param {string} [maskChar] - Character to use for masking
 * @returns {string} The masked text with preserved special characters
 */
export function maskText(text: string, visibleChars: number = 0, maskChar = '•'): string {
  if (!text) return '';

  // If visibleChars is negative or zero, mask all alphanumeric characters
  if (visibleChars <= 0) {
    return text.replace(/[a-zA-Z0-9]/g, maskChar);
  }

  // Split the text into characters
  const chars = text.split('');

  // Count of alphanumeric characters from the end that should remain visible
  let visibleCount = 0;

  // Process the string from the end
  for (let i = chars.length - 1; i >= 0; i--) {
  // If we've already shown enough visible characters, mask this one if it's alphanumeric
    if (visibleCount >= visibleChars && /[a-zA-Z0-9]/.test(chars[i])) {
      chars[i] = maskChar;
    }

    // Count this character if it's alphanumeric
    if (/[a-zA-Z0-9]/.test(chars[i])) {
      visibleCount++;
    }
  }

  return chars.join('');
}

/**
 * Removes double quotes from the first and last characters of a string if both are present
 * @param {string} str - The string to process
 * @returns {string} The string with quotes stripped, or the original string if quotes aren't at both ends
 */
export function stripQuotes(str: string): string {
  return str.length >= 2 && str[0] === '"' && str[str.length - 1] === '"'
    ? str.slice(1, -1)
    : str;
}
